/* Apexzen Animations
   Source: GSAP 3.12.5 + ScrollTrigger (self-hosted in /assets, loaded via theme.liquid)
   Honors prefers-reduced-motion (skips all animations if user opts out)
   Idempotent: safe to re-run on Shopify section reload (theme editor)
   ASCII/cp1252 only - no emoji or unicode decorations
*/

(function () {
  'use strict';

  function init() {
    // Bail out if GSAP failed to load (CDN block, slow network)
    if (typeof window.gsap === 'undefined') {
      return;
    }

    var gsap = window.gsap;
    var ScrollTrigger = window.ScrollTrigger;

    // Respect user accessibility preference
    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion) {
      // Make sure hidden-on-load elements still become visible
      gsap.set('[data-apx-anim]', { clearProps: 'all' });
      return;
    }

    if (ScrollTrigger) {
      gsap.registerPlugin(ScrollTrigger);
    }

    // ---------------------------------------------------------------
    // Animation 1 - Hero reveal stagger (page load)
    // Cible: .apx-hero eyebrow > heading > sub > actions > proof
    // ---------------------------------------------------------------
    var heroRoot = document.querySelector('.apx-hero');
    if (heroRoot) {
      var heroItems = heroRoot.querySelectorAll(
        '.apx-hero__eyebrow, .apx-hero__heading, .apx-hero__sub, .apx-hero__actions, .apx-hero__proof, .apx-hero__media-frame'
      );
      if (heroItems.length > 0) {
        gsap.set(heroItems, { y: 24, opacity: 0 });
        gsap.to(heroItems, {
          y: 0,
          opacity: 1,
          duration: 0.7,
          ease: 'power2.out',
          stagger: 0.08,
          delay: 0.1
        });
      }
    }

    // ---------------------------------------------------------------
    // Animation 2 - Scroll reveal (sections fade in on viewport)
    // Cible: any element with data-apx-anim="fade" or generic section
    //        with class apx-reveal-on-scroll
    // ---------------------------------------------------------------
    if (ScrollTrigger) {
      var revealTargets = document.querySelectorAll(
        '[data-apx-anim="fade"], .apx-reveal-on-scroll'
      );
      revealTargets.forEach(function (el) {
        gsap.set(el, { y: 32, opacity: 0 });
        gsap.to(el, {
          y: 0,
          opacity: 1,
          duration: 0.8,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: el,
            start: 'top 85%',
            toggleActions: 'play none none none'
          }
        });
      });
    }

    // ---------------------------------------------------------------
    // Animation 3 - Stagger children (bundle composition, pillars, value props)
    // Cible: any container with data-apx-anim="stagger" - animates its direct children
    // ---------------------------------------------------------------
    if (ScrollTrigger) {
      var staggerContainers = document.querySelectorAll('[data-apx-anim="stagger"]');
      staggerContainers.forEach(function (container) {
        var children = container.children;
        if (children.length === 0) return;
        gsap.set(children, { y: 20, opacity: 0 });
        gsap.to(children, {
          y: 0,
          opacity: 1,
          duration: 0.6,
          ease: 'power2.out',
          stagger: 0.08,
          scrollTrigger: {
            trigger: container,
            start: 'top 80%',
            toggleActions: 'play none none none'
          }
        });
      });
    }

    // ---------------------------------------------------------------
    // Animation 4 - Subscribe price reveal pulse
    // Cible: .apx-product__price-row--sub when first scrolled into view
    // ---------------------------------------------------------------
    if (ScrollTrigger) {
      var subPriceRows = document.querySelectorAll('.apx-product__price-row--sub');
      subPriceRows.forEach(function (row) {
        gsap.fromTo(
          row,
          { backgroundColor: 'rgba(var(--pillar-color-rgb, 26, 46, 85), 0)' },
          {
            backgroundColor: 'rgba(var(--pillar-color-rgb, 26, 46, 85), 0.08)',
            duration: 0.4,
            ease: 'power1.out',
            yoyo: true,
            repeat: 1,
            scrollTrigger: {
              trigger: row,
              start: 'top 75%',
              toggleActions: 'play none none none'
            }
          }
        );
      });
    }

    // ---------------------------------------------------------------
    // Animation 5 - Hover lift on bundle/product cards
    // Cible: .apx-bundle-showcase__card, .apx-pillar-grid__card
    // Uses GSAP quickTo for performance (no layout thrash on rapid hover)
    // ---------------------------------------------------------------
    var liftCards = document.querySelectorAll(
      '.apx-bundle-showcase__card, .apx-pillar-grid__card, .apx-cross-sell-stack__card'
    );
    liftCards.forEach(function (card) {
      var yTo = gsap.quickTo(card, 'y', { duration: 0.3, ease: 'power2.out' });
      var shadowTo = gsap.quickTo(card, 'boxShadow', { duration: 0.3, ease: 'power2.out' });
      card.addEventListener('mouseenter', function () {
        yTo(-4);
      });
      card.addEventListener('mouseleave', function () {
        yTo(0);
      });
    });

    // ---------------------------------------------------------------
    // Animation 6 - Hero media parallax (subtle scroll-linked depth)
    // Cible: .apx-hero__media-frame - se deplace plus lentement que la page (effet 3D).
    // Transform-only (yPercent) + scrub = compositor GPU, zero cout layout, zero CLS.
    // Se combine proprement avec Animation 1 (le reveal au load utilise y en px, ici yPercent).
    // ---------------------------------------------------------------
    if (ScrollTrigger) {
      var heroMediaFrame = document.querySelector('.apx-hero__media-frame');
      if (heroMediaFrame) {
        gsap.to(heroMediaFrame, {
          yPercent: -10,
          ease: 'none',
          scrollTrigger: {
            trigger: heroMediaFrame,
            start: 'top 20%',
            end: 'bottom top',
            scrub: 0.5
          }
        });
      }
    }

    // ---------------------------------------------------------------
    // Animation 7 - Count-up on numbers entering the viewport
    // Cible: [data-apx-countup] with data-count-to (and optional
    //        data-count-decimals / data-count-prefix / data-count-suffix).
    // The element's HTML already holds the final value, so reduced-motion
    // and no-JS users see the real number; JS only animates 0 -> value.
    // ---------------------------------------------------------------
    if (ScrollTrigger) {
      document.querySelectorAll('[data-apx-countup]').forEach(function (el) {
        var to = parseFloat(el.getAttribute('data-count-to'));
        if (isNaN(to)) return;
        var dec = parseInt(el.getAttribute('data-count-decimals') || '0', 10);
        var prefix = el.getAttribute('data-count-prefix') || '';
        var suffix = el.getAttribute('data-count-suffix') || '';
        var obj = { v: 0 };
        gsap.to(obj, {
          v: to,
          duration: 1.4,
          ease: 'power2.out',
          scrollTrigger: { trigger: el, start: 'top 85%', toggleActions: 'play none none none' },
          onUpdate: function () { el.textContent = prefix + obj.v.toFixed(dec) + suffix; },
          onComplete: function () { el.textContent = prefix + to.toFixed(dec) + suffix; }
        });
      });
    }

    // ---------------------------------------------------------------
    // Animation 8 - Bar fill on scroll
    // Cible: [data-apx-bar] with data-bar-pct (0-100). The bar's inline
    //        width is already the final %, so it renders correctly without
    //        JS; here we animate it growing from 0.
    // ---------------------------------------------------------------
    if (ScrollTrigger) {
      document.querySelectorAll('[data-apx-bar]').forEach(function (el) {
        var pct = parseFloat(el.getAttribute('data-bar-pct'));
        if (isNaN(pct)) return;
        gsap.fromTo(
          el,
          { width: '0%' },
          {
            width: pct + '%',
            duration: 1.2,
            ease: 'power2.out',
            scrollTrigger: { trigger: el, start: 'top 90%', toggleActions: 'play none none none' }
          }
        );
      });
    }

    // ---------------------------------------------------------------
    // Shopify section reload hook (theme editor + section render API)
    // ---------------------------------------------------------------
    if (typeof Shopify !== 'undefined' && Shopify.designMode) {
      document.addEventListener('shopify:section:load', function () {
        if (ScrollTrigger) {
          ScrollTrigger.refresh();
        }
      });
    }
  }

  // GSAP is loaded with defer; wait for it
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
